// Renders a survey's questions for answering (public /survey/:token) or as the admin's preview.
// Conditions ("show only if…") and required checks follow the same rules as the server
// (services/surveys.js: visible / validateAnswers).
import React from 'react';
import {
  Box, Stack, Typography, TextField, Radio, RadioGroup, FormControlLabel, Checkbox, MenuItem, Rating, ToggleButton,
  ToggleButtonGroup, Paper, Divider,
} from '@mui/material';

export function isVisible(q, answers, byId) {
  if (!q.condition) return true;
  const dep = byId[q.condition.questionId];
  if (!dep || !isVisible(dep, answers, byId)) return false;
  const a = answers[q.condition.questionId];
  const v = q.condition.value;
  const empty = a === undefined || a === null || a === '' || (Array.isArray(a) && !a.length);
  switch (q.condition.operator) {
    case 'answered': return !empty;
    case 'equals': return !empty && String(Array.isArray(a) ? a.join(',') : a) === String(v);
    case 'not_equals': return !empty && String(Array.isArray(a) ? a.join(',') : a) !== String(v);
    case 'includes': return Array.isArray(a) ? a.map(String).includes(String(v)) : String(a ?? '').includes(String(v));
    case 'gte': return !empty && Number(a) >= Number(v);
    case 'lte': return !empty && Number(a) <= Number(v);
    default: return true;
  }
}

// The required questions still unanswered (shown ones only)
export function missingRequired(questions, answers) {
  const byId = Object.fromEntries(questions.map(q => [q.id, q]));
  return questions.filter(q => q.type !== 'section' && q.required && isVisible(q, answers, byId)).filter(q => {
    const a = answers[q.id];
    return a === undefined || a === null || a === '' || (Array.isArray(a) && !a.length);
  });
}

function Scale({ q, value, onChange, lo, hi }) {
  return (
    <Box>
      <ToggleButtonGroup exclusive value={value ?? null} onChange={(_, v) => v !== null && onChange(v)} size="small" sx={{ flexWrap: 'wrap' }}>
        {Array.from({ length: hi - lo + 1 }, (_, k) => lo + k).map(n => (
          <ToggleButton key={n} value={n} sx={{ minWidth: 44, fontWeight: 700 }} aria-label={String(n)}>{n}</ToggleButton>
        ))}
      </ToggleButtonGroup>
      {(q.scaleLabels?.low || q.scaleLabels?.high) && (
        <Stack direction="row" justifyContent="space-between" sx={{ maxWidth: (hi - lo + 1) * 46 }} mt={0.5}>
          <Typography variant="caption" color="text.secondary">{q.scaleLabels?.low}</Typography>
          <Typography variant="caption" color="text.secondary">{q.scaleLabels?.high}</Typography>
        </Stack>
      )}
    </Box>
  );
}

function Answer({ q, value, onChange, disabled }) {
  switch (q.type) {
    case 'rating':
      return <Rating size="large" value={value ?? null} onChange={(_, v) => onChange(v)} disabled={disabled} />;
    case 'nps':
      return <Scale q={{ scaleLabels: { low: 'Not at all likely', high: 'Extremely likely', ...(q.scaleLabels || {}) } }} value={value} onChange={onChange} lo={0} hi={10} />;
    case 'scale':
      return <Scale q={q} value={value} onChange={onChange} lo={q.min ?? 1} hi={q.max ?? 10} />;
    case 'single':
      return (
        <RadioGroup value={value ?? ''} onChange={e => onChange(e.target.value)}>
          {q.options.map(o => <FormControlLabel key={o} value={o} control={<Radio disabled={disabled} />} label={o} />)}
        </RadioGroup>
      );
    case 'multiple': {
      const list = Array.isArray(value) ? value : [];
      return (
        <Stack>
          {q.options.map(o => (
            <FormControlLabel key={o} label={o} control={
              <Checkbox disabled={disabled || (q.maxChoices && list.length >= q.maxChoices && !list.includes(o))} checked={list.includes(o)}
                onChange={e => onChange(e.target.checked ? [...list, o] : list.filter(x => x !== o))} />
            } />
          ))}
          {q.maxChoices && <Typography variant="caption" color="text.secondary">Choose up to {q.maxChoices}</Typography>}
        </Stack>
      );
    }
    case 'dropdown':
      return (
        <TextField select size="small" value={value ?? ''} onChange={e => onChange(e.target.value)} sx={{ minWidth: 260 }} disabled={disabled}>
          <MenuItem value=""><em>Choose…</em></MenuItem>
          {q.options.map(o => <MenuItem key={o} value={o}>{o}</MenuItem>)}
        </TextField>
      );
    case 'yesno':
      return (
        <ToggleButtonGroup exclusive value={value ?? null} onChange={(_, v) => v !== null && onChange(v)} size="small">
          <ToggleButton value="yes" sx={{ minWidth: 80 }}>Yes</ToggleButton>
          <ToggleButton value="no" sx={{ minWidth: 80 }}>No</ToggleButton>
        </ToggleButtonGroup>
      );
    case 'short_text':
      return <TextField size="small" fullWidth value={value ?? ''} onChange={e => onChange(e.target.value)} placeholder={q.placeholder} inputProps={{ maxLength: 300 }} disabled={disabled} />;
    case 'long_text':
      return <TextField fullWidth multiline minRows={3} value={value ?? ''} onChange={e => onChange(e.target.value)} placeholder={q.placeholder} inputProps={{ maxLength: 5000 }} disabled={disabled} />;
    case 'number':
      return <TextField size="small" type="number" value={value ?? ''} onChange={e => onChange(e.target.value === '' ? '' : Number(e.target.value))} sx={{ width: 200 }} disabled={disabled} />;
    case 'date':
      return <TextField size="small" type="date" value={value ?? ''} onChange={e => onChange(e.target.value)} InputLabelProps={{ shrink: true }} disabled={disabled} />;
    default:
      return null;
  }
}

export default function SurveyForm({ questions, answers, onChange, showErrors, disabled }) {
  const byId = Object.fromEntries(questions.map(q => [q.id, q]));
  const missing = new Set(showErrors ? missingRequired(questions, answers).map(q => q.id) : []);
  let n = 0;
  return (
    <Stack spacing={2}>
      {questions.filter(q => isVisible(q, answers, byId)).map(q => {
        if (q.type === 'section') {
          return (
            <Box key={q.id} pt={1}>
              <Divider sx={{ mb: 1.5 }} />
              <Typography variant="h6" fontWeight={700}>{q.label}</Typography>
              {q.help && <Typography variant="body2" color="text.secondary">{q.help}</Typography>}
            </Box>
          );
        }
        n += 1;
        return (
          <Paper key={q.id} variant="outlined" sx={{ p: 2, borderRadius: 2, borderColor: missing.has(q.id) ? 'error.main' : 'divider' }}>
            <Typography fontWeight={600} mb={0.5}>
              {n}. {q.label}{q.required && <Box component="span" color="error.main"> *</Box>}
            </Typography>
            {q.help && <Typography variant="body2" color="text.secondary" mb={1}>{q.help}</Typography>}
            <Answer q={q} value={answers[q.id]} onChange={v => onChange({ ...answers, [q.id]: v })} disabled={disabled} />
            {missing.has(q.id) && <Typography variant="caption" color="error" display="block" mt={0.5}>Please answer this question</Typography>}
          </Paper>
        );
      })}
    </Stack>
  );
}
